import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const field = {
  width: '100%', padding: '13px 14px 13px 42px', borderRadius: '11px',
  border: '1px solid var(--mist)', fontSize: '0.92rem', fontFamily: 'DM Sans',
  background: 'var(--white)', color: 'var(--charcoal)', outline: 'none',
};

function Shell({ title, subtitle, children, footer }) {
  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        style={{ width: '100%', maxWidth: '420px', margin: '40px 20px', background: '#fff', border: '1px solid var(--mist)', borderRadius: '22px', padding: '38px 34px', boxShadow: 'var(--shadow)' }}>
        <h1 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.7rem', color: 'var(--forest)', marginBottom: '6px' }}>{title}</h1>
        <p style={{ color: 'var(--mid)', fontSize: '0.9rem', marginBottom: '26px' }}>{subtitle}</p>
        {children}
        <div style={{ textAlign: 'center', marginTop: '22px', fontSize: '0.85rem', color: 'var(--mid)' }}>{footer}</div>
      </motion.div>
    </div>
  );
}

const IconField = ({ Icon, ...props }) => (
  <div style={{ position: 'relative', marginBottom: '14px' }}>
    <Icon size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mid)' }} />
    <input style={field} {...props} />
  </div>
);

const submitBtn = (busy) => ({
  width: '100%', padding: '14px', borderRadius: '11px', background: busy ? 'var(--mist)' : 'var(--forest)',
  color: '#fff', fontFamily: 'Syne', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.04em',
  textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  cursor: busy ? 'not-allowed' : 'pointer',
});

const errorBox = { background: '#fdecec', color: '#c0392b', fontSize: '0.82rem', padding: '10px 12px', borderRadius: '10px', marginBottom: '14px' };

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await login(email, password);
      navigate('/account');
    } catch (err) {
      setError(err.offline ? 'Server not reachable. Start the backend and try again.' : (err.message || 'Login failed'));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Welcome back" subtitle="Log in to your Mind Blown account."
      footer={<>Don't have an account? <Link to="/register" style={{ color: 'var(--forest)', fontWeight: 700 }}>Create one</Link></>}>
      <form onSubmit={submit}>
        {error && <div style={errorBox}>{error}</div>}
        <IconField Icon={Mail} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <IconField Icon={Lock} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button type="submit" disabled={busy} style={submitBtn(busy)}>{busy ? 'Logging in…' : 'Log in'} <ArrowRight size={15} /></button>
      </form>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await register(form);
      navigate('/account');
    } catch (err) {
      setError(err.offline ? 'Server not reachable. Start the backend and try again.' : (err.message || 'Registration failed'));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Create account" subtitle="Join Mind Blown for faster checkout and order tracking."
      footer={<>Already have an account? <Link to="/login" style={{ color: 'var(--forest)', fontWeight: 700 }}>Log in</Link></>}>
      <form onSubmit={submit}>
        {error && <div style={errorBox}>{error}</div>}
        <IconField Icon={User} placeholder="Full name" value={form.name} onChange={set('name')} required />
        <IconField Icon={Mail} type="email" placeholder="Email" value={form.email} onChange={set('email')} required />
        <IconField Icon={Lock} type="password" placeholder="Password (min 8 chars)" value={form.password} onChange={set('password')} required />
        <IconField Icon={User} placeholder="Phone (optional)" value={form.phone} onChange={set('phone')} />
        <button type="submit" disabled={busy} style={submitBtn(busy)}>{busy ? 'Creating…' : 'Create account'} <ArrowRight size={15} /></button>
      </form>
    </Shell>
  );
}
